import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { IconName } from '../../assets/Icon/Icon';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import {
  slideDownAndFade,
  slideLeftAndFade,
  slideRightAndFade,
  slideUpAndFade,
} from '../../common/animations';
import { fade, palette } from '../../common/colors';
import { zIndexes } from '../../common/z_indexes';
import { Button } from '../../controls/Button/Button';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { VStack } from '../../utilities/Stack/VStack';

const TourIndicators = styled('div', {
  display: 'flex',
  gap: '$4',
  alignItems: 'center',
  padding: '$2',
  backgroundColor: colors.bgNeutralLight,
  borderRadius: '99em',

  [darkThemeSelector]: {
    backgroundColor: colors.bgNeutralDark,
  },
});
TourIndicators.displayName = 'TourIndicators';

const TourIndicatorsDot = styled('div', {
  width: '$8',
  height: '$8',
  borderRadius: '50%',
  backgroundColor: colors.gray200,
  transition: 'all 200ms ease',

  [darkThemeSelector]: {
    backgroundColor: colors.gray700,
  },

  variants: {
    selected: {
      true: {
        backgroundColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.iconBrandDark,
        },
      },
      false: {},
    },
  },
});
TourIndicatorsDot.displayName = 'TourIndicatorsDot';

const TourDivider = styled(Divider);
TourDivider.displayName = 'TourDivider';

const TourControlsButtons = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,

  '&:last-child': {
    justifyContent: 'flex-end',
  },
});
TourControlsButtons.displayName = 'TourControlsButtons';

const TourControls = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$8',
  widthAll: '100%',
});
TourControls.displayName = 'TourControls';

const TourPopover = styled('div', {
  position: 'absolute',
  zIndex: zIndexes.overlay + 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  widthAll: '$280',
  padding: '$8 $12',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayAppLight,
  borderRadius: '$12',
  transition: 'opacity 200ms ease-out',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayAppDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '250ms',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',

    '&[data-side="top"]': { animationName: slideUpAndFade },
    '&[data-side="right"]': { animationName: slideRightAndFade },
    '&[data-side="bottom"]': { animationName: slideDownAndFade },
    '&[data-side="left"]': { animationName: slideLeftAndFade },
  },
});
TourPopover.displayName = 'TourPopover';

const TourPulse = styled('div', {
  position: 'absolute',
  inset: '-$4',
  borderRadius: '$16',
  border: `4px solid ${colors.chartFillBrandLight}`,
  pointerEvents: 'none',
  animation: `${keyframes({
    '0%': { opacity: 0, transform: 'scale(1)' },
    '50%': { opacity: 0.6, transform: 'scale(1.02)' },
    '100%': { opacity: 0, transform: 'scale(1.04)' },
  })} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,

  [darkThemeSelector]: {
    border: `4px solid ${colors.chartFillBrandDark}`,
  },

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 0.3,
  },
});
TourPulse.displayName = 'TourPulse';

const spotlightGrowLight = keyframes({
  from: {
    boxShadow: `0 0 0 0 ${fade(palette.gray900, 0.12)}`,
  },
  to: {
    boxShadow: `0 0 0 9999px ${fade(palette.gray900, 0.12)}`,
  },
});

const spotlightGrowDark = keyframes({
  from: {
    boxShadow: `0 0 0 0 ${fade(palette.gray900, 0.3)}`,
  },
  to: {
    boxShadow: `0 0 0 9999px ${fade(palette.gray900, 0.3)}`,
  },
});

const TourSpotlight = styled('div', {
  position: 'absolute',
  boxShadow: `0 0 0 9999px ${fade(palette.gray900, 0.6)}`,
  borderRadius: '$12',
  pointerEvents: 'none',
  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${spotlightGrowLight} 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,

  [darkThemeSelector]: {
    animation: `${spotlightGrowDark} 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
  },

  '@media (prefers-reduced-motion: reduce)': {
    boxShadow: `0 0 0 9999px ${fade(palette.gray900, 0.6)}`,
    animation: 'none',

    [darkThemeSelector]: {
      boxShadow: `0 0 0 9999px ${fade(palette.gray900, 0.6)}`,
    },
  },
});
TourSpotlight.displayName = 'TourSpotlight';

const overlayFadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const TourOverlay = styled('div', {
  position: 'fixed',
  inset: 0,
  zIndex: zIndexes.overlay,
  pointerEvents: 'auto',
  animation: `${overlayFadeIn} 300ms ease-out`,
});
TourOverlay.displayName = 'TourOverlay';

export type TourStep = {
  /**
   * Description of the tour step
   */
  description?: ReactNode;
  /**
   * Heading of the tour step
   */
  heading?: ReactNode;
  /**
   * Optional icon to display
   */
  icon?: IconName;
  /**
   * Preferred side to place the popover relative to the target
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * CSS selector or HTMLElement reference for the element to highlight
   */
  target: string | HTMLElement;
  /**
   * Whether this step should be visible in the tour.
   * If false or if the function returns false, this step will be skipped.
   * Defaults to true if not provided.
   */
  isVisible?: boolean | (() => boolean);
};

export type TourProps = {
  /**
   * Array of tour steps
   */
  steps: TourStep[];
  /**
   * Whether the tour is open
   */
  isOpen: boolean;
  /**
   * Callback when the tour is closed/finished
   */
  onClose: () => void;
  /**
   * Initial step index
   */
  initialStep?: number;
  /**
   * Callback when a step changes
   */
  onStepChange?: (stepIndex: number) => void;
};

type Position = {
  top: number;
  left: number;
  width: number;
  height: number;
};

function getElementPosition(element: HTMLElement): Position {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    width: rect.width,
    height: rect.height,
  };
}

function getPopoverPosition(
  targetRect: Position,
  popoverWidth: number,
  popoverHeight: number,
  placement: 'top' | 'right' | 'bottom' | 'left' = 'bottom',
  offset = 16,
): { top: number; left: number; side: string } {
  const { innerWidth: viewportWidth, innerHeight: viewportHeight, scrollY, scrollX } = window;

  const padding = 16;
  const minLeft = scrollX + padding;
  const maxLeft = scrollX + viewportWidth - popoverWidth - padding;
  const minTop = scrollY + padding;
  const maxTop = scrollY + viewportHeight - popoverHeight - padding;

  // Try the preferred placement first, then fallback to others if it doesn't fit
  const placements: Array<'top' | 'right' | 'bottom' | 'left'> = [placement];
  const allPlacements: Array<'top' | 'right' | 'bottom' | 'left'> = [
    'bottom',
    'top',
    'right',
    'left',
  ];
  allPlacements.forEach((p) => {
    if (p !== placement) placements.push(p);
  });

  let bestPlacement = placement;
  let bestTop = 0;
  let bestLeft = 0;
  let bestScore = -Infinity;

  for (const side of placements) {
    let top = 0;
    let left = 0;

    switch (side) {
      case 'top':
        top = targetRect.top - popoverHeight - offset;
        left = targetRect.left + targetRect.width / 2 - popoverWidth / 2;
        break;
      case 'bottom':
        top = targetRect.top + targetRect.height + offset;
        left = targetRect.left + targetRect.width / 2 - popoverWidth / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - popoverHeight / 2;
        left = targetRect.left - popoverWidth - offset;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - popoverHeight / 2;
        left = targetRect.left + targetRect.width + offset;
        break;
    }

    // Calculate how well this placement fits
    const fitsTop = top >= minTop;
    const fitsBottom = top + popoverHeight <= scrollY + viewportHeight - padding;
    const fitsLeft = left >= minLeft;
    const fitsRight = left + popoverWidth <= scrollX + viewportWidth - padding;

    // Score this placement (higher is better)
    let score = 0;
    if (fitsTop) score += 1;
    if (fitsBottom) score += 1;
    if (fitsLeft) score += 1;
    if (fitsRight) score += 1;

    // Prefer the requested placement
    if (side === placement) score += 0.5;

    if (score > bestScore) {
      bestScore = score;
      bestPlacement = side;
      bestTop = top;
      bestLeft = left;
    }

    // If this placement fits perfectly, use it
    if (score >= 4) {
      break;
    }
  }

  // Clamp the final position to viewport
  bestLeft = Math.max(minLeft, Math.min(maxLeft, bestLeft));
  bestTop = Math.max(minTop, Math.min(maxTop, bestTop));

  return { top: bestTop, left: bestLeft, side: bestPlacement };
}

export function Tour({ steps, isOpen, onClose, initialStep = 0, onStepChange }: TourProps) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [targetPosition, setTargetPosition] = useState<Position | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
    side: string;
  } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);

  // Filter steps based on visibility
  const visibleSteps = useMemo(
    () =>
      steps.filter((step) => {
        if (step.isVisible === undefined) return true;
        return typeof step.isVisible === 'function' ? step.isVisible() : step.isVisible;
      }),
    [steps],
  );

  const step = visibleSteps[currentStep];
  const isLastStep = currentStep === visibleSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const updatePositions = useCallback(() => {
    if (!step || !isOpen) return;

    const element =
      typeof step.target === 'string'
        ? document.querySelector<HTMLElement>(step.target)
        : step.target;

    if (!element) return;

    targetRef.current = element;
    const targetRect = getElementPosition(element);
    setTargetPosition(targetRect);

    if (popoverRef.current) {
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const popoverPos = getPopoverPosition(
        targetRect,
        popoverRect.width,
        popoverRect.height,
        step.placement,
      );
      setPopoverPosition(popoverPos);
    }
  }, [step, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    updatePositions();

    // Update on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(updatePositions);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Use ResizeObserver for target element changes
    const resizeObserver = new ResizeObserver(handleUpdate);
    if (targetRef.current) {
      resizeObserver.observe(targetRef.current);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      resizeObserver.disconnect();
    };
  }, [isOpen, updatePositions]);

  // Re-measure popover after it renders
  useEffect(() => {
    if (isOpen && targetPosition) {
      requestAnimationFrame(updatePositions);
    }
  }, [isOpen, targetPosition, currentStep, updatePositions]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < visibleSteps.length) {
        setCurrentStep(stepIndex);
        onStepChange?.(stepIndex);
      }
    },
    [visibleSteps.length, onStepChange],
  );

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onClose();
    } else {
      goToStep(currentStep + 1);
    }
  }, [currentStep, isLastStep, goToStep, onClose]);

  const handlePrevious = useCallback(() => {
    goToStep(currentStep - 1);
  }, [currentStep, goToStep]);

  const handleSkip = useCallback(() => {
    onClose();
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          handleSkip();
          break;
        case 'ArrowRight':
          if (!isLastStep) {
            event.preventDefault();
            handleNext();
          }
          break;
        case 'ArrowLeft':
          if (!isFirstStep) {
            event.preventDefault();
            handlePrevious();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line consistent-return
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleNext, handlePrevious, handleSkip, isFirstStep, isLastStep]);

  const renderContent = useMemo(() => {
    if (!isOpen || !targetPosition || !step) return null;

    return (
      <>
        <TourSpotlight
          style={{
            top: targetPosition.top - 8,
            left: targetPosition.left - 8,
            width: targetPosition.width + 16,
            height: targetPosition.height + 16,
          }}
        >
          <TourPulse />
        </TourSpotlight>
        <TourPopover
          ref={popoverRef}
          data-side={popoverPosition?.side || 'bottom'}
          style={{
            top: popoverPosition?.top ?? 0,
            left: popoverPosition?.left ?? 0,
            opacity: popoverPosition ? 1 : 0,
            pointerEvents: popoverPosition ? 'auto' : 'none',
          }}
        >
          <AlignStack
            gap={6}
            preset="body"
            start={step.icon && <Icon icon={step.icon} size={16} />}
          >
            <VStack>
              {step.heading && <Text weight="bold">{step.heading}</Text>}
              {step.description && <Small variant="neutral">{step.description}</Small>}
            </VStack>
          </AlignStack>
          <TourDivider direction="row" appearance="thin" variant="neutral" />
          <TourControls>
            <TourControlsButtons>
              <Button onClick={handleSkip} ghost size="small" variant="secondary">
                Skip
              </Button>
            </TourControlsButtons>
            {visibleSteps.length > 1 && (
              <TourIndicators>
                {visibleSteps.map((tourStep, index) => (
                  <TourIndicatorsDot
                    key={`step-${tourStep.heading}`}
                    selected={index === currentStep}
                  />
                ))}
              </TourIndicators>
            )}
            <TourControlsButtons>
              {visibleSteps.length > 1 && (
                <Button
                  onClick={handlePrevious}
                  arrangement="hidden-label"
                  disabled={isFirstStep}
                  ghost
                  icon="arrow-left"
                  size="small"
                  variant="secondary"
                >
                  Previous
                </Button>
              )}
              <Button
                onClick={handleNext}
                arrangement="hidden-label"
                ghost
                icon={isLastStep ? 'checkmark-circle' : 'arrow-right'}
                size="small"
                variant="secondary"
              >
                {isLastStep ? 'Finish' : 'Next'}
              </Button>
            </TourControlsButtons>
          </TourControls>
        </TourPopover>
        <TourOverlay onClick={handleSkip} />
      </>
    );
  }, [
    isOpen,
    targetPosition,
    popoverPosition,
    step,
    currentStep,
    visibleSteps,
    isFirstStep,
    isLastStep,
    handleSkip,
    handleNext,
    handlePrevious,
  ]);

  if (!isOpen) return null;

  return createPortal(renderContent, document.body);
}
